// @@EQUIPMENT_LEASES_v87@@ shared bits for the HQ › Equipment pages.
import React from 'react';

export const KINDS = [
  ['card_reader', 'Card reader'], ['pos_terminal', 'POS terminal'], ['tablet', 'Tablet'], ['phone', 'Phone'],
  ['printer', 'Printer'], ['scanner', 'Scanner'], ['accessory', 'Accessory'], ['other', 'Other'],
];
export const KIND_LABEL = Object.fromEntries(KINDS);
export const CONDITIONS = [['new', 'New'], ['good', 'Good'], ['fair', 'Fair'], ['damaged', 'Damaged']];
export const UNIT_STATUSES = [['available', 'Available'], ['maintenance', 'Maintenance'], ['retired', 'Retired'], ['lost', 'Lost']];
export const LEASE_LABEL = { draft: 'Draft', sent: 'Awaiting client', active: 'Active', returned: 'Returned', cancelled: 'Cancelled' };
export const UN_LABEL = {
  un3091: 'UN 3091 — lithium metal batteries contained in or packed with equipment',
  un3481: 'UN 3481 — lithium ion batteries contained in or packed with equipment',
};

const TONES = {
  blue: ['rgba(110,168,255,.12)', '#6ea8ff', 'rgba(110,168,255,.22)'],
  amber: ['rgba(232,193,90,.12)', '#e8c15a', 'rgba(232,193,90,.22)'],
  green: ['rgba(80,220,140,.12)', '#5fdc97', 'rgba(80,220,140,.22)'],
  red: ['rgba(255,90,82,.1)', '#ff7a73', 'rgba(255,90,82,.22)'],
  purple: ['rgba(180,140,255,.12)', '#b48cff', 'rgba(180,140,255,.22)'],
  grey: ['rgba(138,138,144,.12)', '#a1a1a8', 'rgba(138,138,144,.22)'],
};
export function pill(tone) {
  const [bg, fg, bd] = TONES[tone] || TONES.grey;
  return { display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: 'nowrap' };
}
const LEASE_TONE = { draft: 'grey', sent: 'amber', active: 'green', returned: 'blue', cancelled: 'grey' };
export const leasePill = (s) => pill(LEASE_TONE[s]);

// Unit availability as shown in lists: derived from its open lease, else its status.
export function unitState(u) {
  if (u.lease) {
    if (u.lease.status === 'active') return { label: 'On lease', tone: 'purple' };
    if (u.lease.status === 'sent') return { label: 'Awaiting client', tone: 'amber' };
    return { label: 'Reserved (draft)', tone: 'grey' };
  }
  return {
    available: { label: 'Available', tone: 'green' }, maintenance: { label: 'Maintenance', tone: 'amber' },
    retired: { label: 'Retired', tone: 'grey' }, lost: { label: 'Lost', tone: 'red' },
  }[u.status] || { label: u.status, tone: 'grey' };
}

export const LINK = { color: '#31b44b', textDecoration: 'none' };
export const MUTED = { color: 'var(--hq-muted)', fontSize: 12 };
export const LABEL = { display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hq-muted)', fontWeight: 600, marginBottom: 6 };
export const TEXTAREA = { width: '100%', minHeight: 90, background: 'var(--hq-field)', color: 'var(--hq-txt)', border: '1px solid var(--hq-line)', borderRadius: 9, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box' };
export const GHOST = { width: 'auto', padding: '0 14px', marginTop: 0, height: 38 };

export function money(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// YYYY-MM-DD -> local date. Never new Date('YYYY-MM-DD') (UTC -> shows the day before).
export function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
export function fmtTs(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return '—'; }
}

export function BatteryBadges({ u, empty = '—' }) {
  const marks = [];
  if (u.un3091) marks.push('UN 3091');
  if (u.un3481) marks.push('UN 3481');
  if (!marks.length) return u.has_battery ? <span style={pill('amber')} title="Contains a battery — no UN mark set">Battery</span> : <span style={MUTED}>{empty}</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {marks.map((m) => (
        <span key={m} style={{ ...pill('amber'), fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', textTransform: 'none' }}
          title={m === 'UN 3091' ? UN_LABEL.un3091 : UN_LABEL.un3481}>{m}</span>
      ))}
    </span>
  );
}

export async function readJson(res) {
  try { return await res.json(); } catch { return {}; }
}
export const JSON_HEADERS = { 'Content-Type': 'application/json' };
// "Square" + "Square Handheld" -> "Square Handheld" (don't repeat the make).
export function makeModel(make, model) {
  const a = String(make || '').trim(), b = String(model || '').trim();
  if (a && b && b.toLowerCase().startsWith(a.toLowerCase())) return b;
  return [a, b].filter(Boolean).join(' ');
}

// Download an authenticated file (a plain <a href> would skip the Bearer header).
export async function downloadBlob(apiFetch, path, filename) {
  const res = await apiFetch(path);
  if (!res.ok) return false;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

// ── Inventory unit form (add + edit) ─────────────────────────────────────────
export const BLANK_UNIT = {
  asset_tag: '', kind: 'card_reader', make: '', model: '', os_version: '', device_id: '', replacement_value: '',
  condition: 'good', status: 'available', has_battery: false, un3091: false, un3481: false, battery_notes: '', notes: '',
};

export function UnitForm({ value, onChange, disabled }) {
  const v = value;
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const field = (k, label, props = {}) => (
    <label style={{ display: 'block', flex: props.flex || '1 1 200px' }}>
      <span style={LABEL}>{label}</span>
      <input className="hq-input" style={{ width: '100%', boxSizing: 'border-box' }} value={v[k] ?? ''} onChange={set(k)} disabled={disabled} {...props.input} />
    </label>
  );
  const select = (k, label, opts) => (
    <label style={{ display: 'block', flex: '1 1 160px' }}>
      <span style={LABEL}>{label}</span>
      <select className="hq-select" style={{ width: '100%' }} value={v[k]} onChange={set(k)} disabled={disabled}>
        {opts.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
      </select>
    </label>
  );
  const check = (k, label, sub) => (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: disabled ? 'default' : 'pointer', fontSize: 14 }}>
      <input type="checkbox" checked={!!v[k]} onChange={(e) => {
        const next = { ...v, [k]: e.target.checked };
        if (k !== 'has_battery' && e.target.checked) next.has_battery = true;
        if (k === 'has_battery' && !e.target.checked) { next.un3091 = false; next.un3481 = false; }
        onChange(next);
      }} disabled={disabled} style={{ marginTop: 3 }} />
      <span>{label}{sub && <span style={{ ...MUTED, display: 'block' }}>{sub}</span>}</span>
    </label>
  );
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {field('asset_tag', 'Asset tag *', { input: { placeholder: 'XYZ-POS-01', maxLength: 60 } })}
        {select('kind', 'Type', KINDS)}
        {field('replacement_value', 'Replacement value ($)', { input: { inputMode: 'decimal', placeholder: '399.99' } })}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {field('make', 'Make', { input: { placeholder: 'Square' } })}
        {field('model', 'Model', { flex: '2 1 260px', input: { placeholder: 'Square Handheld (1st Gen, v1)' } })}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {field('device_id', 'Device ID / serial', { input: { placeholder: '521WS21606001608' } })}
        {field('os_version', 'OS version', { input: { placeholder: '7.19.0043' } })}
        {select('condition', 'Condition', CONDITIONS)}
        {select('status', 'Status', UNIT_STATUSES)}
      </div>
      <div style={{ border: '1px solid var(--hq-line)', borderRadius: 12, padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Batteries</div>
        {check('has_battery', 'Contains internal batteries')}
        {check('un3481', 'UN 3481', 'Lithium ion batteries contained in or packed with equipment (most handheld readers, phones, tablets)')}
        {check('un3091', 'UN 3091', 'Lithium metal batteries contained in or packed with equipment (non-rechargeable lithium cells)')}
        <label style={{ display: 'block' }}>
          <span style={LABEL}>Battery notes</span>
          <input className="hq-input" style={{ width: '100%', boxSizing: 'border-box' }} value={v.battery_notes || ''} onChange={set('battery_notes')} disabled={disabled}
            placeholder="e.g. 3.7V 2,600 mAh Li-ion, non-removable" maxLength={500} />
        </label>
      </div>
      <label style={{ display: 'block' }}>
        <span style={LABEL}>Internal notes</span>
        <textarea style={{ ...TEXTAREA, minHeight: 70 }} value={v.notes || ''} onChange={set('notes')} disabled={disabled} maxLength={2000} />
      </label>
    </div>
  );
}
